import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import { productService } from "services/product.service";
import authStore from "stores/auth/AuthStore";
import { Product } from "types/product.type";
import { ProductStats } from "types/stockMovement.type";
import buildProductFormData from "utils/buildProductFormData";

class ProductStore {
  products: Product[] = [];
  loadingForm: boolean = false;
  loadingUpdatesItem = new Map<number, boolean>();
  loadingDeletesItem = new Map<number, boolean>();
  loading: boolean = false;
  loadingStats: boolean = false;

  // stats specific product
  productStats: ProductStats | null = null;
  loadingProductStats: boolean = false;

  totalProducts: number = 0;
  currentPage: number = 1;
  rowsPerPage: number = 10;

  filters = {
    name: "",
    reference: "",
    category: "",
    subcategory: "",
    status: "",
    isOnPromo: "",
    subCompany: "",
  };

  stats = {
    totalProducts: 0,
    totalArchived: 0,
    totalOnPromo: 0,
    inStockProducts: 0,
    outOfStockProducts: 0,
    returned: 0,
    defective: 0,
    inactive: 0,
    active: 0,
  };

  constructor() {
    makeAutoObservable(this);
  }

  async fetchStats() {
    if (!authStore.isAuthenticated()) return;
    this.loadingStats = true;

    try {
      const res = await productService.getStats();
      runInAction(() => {
        this.stats = res;
      });
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des statistiques"
      );
      runInAction(() => {
        this.stats = {
          totalProducts: 0,
          totalArchived: 0,
          totalOnPromo: 0,
          inStockProducts: 0,
          outOfStockProducts: 0,
          returned: 0,
          defective: 0,
          inactive: 0,
          active: 0,
        };
      });
    } finally {
      runInAction(() => {
        this.loadingStats = false;
      });
    }
  }

  async fetchProducts(params: {
    page?: number;
    limit?: number;
    name?: string;
    reference?: string;
    category?: string;
    subcategory?: string;
    status?: string;
    isOnPromo?: string;
    subCompany?: string;
  }) {
    if (!authStore.isAuthenticated()) return;

    this.loading = true;
    try {
      const page = Math.max(1, params.page || 1);
      const limit = Math.max(1, params.limit || 10);

      this.filters = {
        name: params.name || "",
        reference: params.reference || "",
        category: params.category || "",
        subcategory: params.subcategory || "",
        status: params.status || "",
        isOnPromo: params.isOnPromo || "",
        subCompany: params.subCompany || "",
      };

      const res = await productService.getAll({
        page,
        limit,
        ...this.filters,
      });

      runInAction(() => {
        this.products = res.data || [];
        this.totalProducts = res.total || 0;
        this.currentPage = res.page || page;
        this.rowsPerPage = res.limit || limit;
      });
    } catch (error: any) {
      if (
        error?.response?.data?.type === "role_changed" ||
        error?.response?.data?.type === "account_inactive"
      ) {
        return;
      }
      toast.error(
        error?.response?.data?.message ||
          "Erreur lors du chargement des produits"
      );
      runInAction(() => {
        this.products = [];
        this.totalProducts = 0;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async createProduct(data: Partial<Product>) {
    this.loadingForm = true;
    try {
      const productData = buildProductFormData(data);
      const savedProduct = await productService.create(productData);

      runInAction(() => {
        this.products = [...this.products, savedProduct];
      });
      await this.fetchStats();
      toast.success("Produit créé !");
      return savedProduct;
    } catch (error) {
      // throw error;
    } finally {
      runInAction(() => {
        this.loadingForm = false;
      });
    }
  }

  async updateProduct(id: number, data: Partial<Product>) {
    this.loadingForm = true;
    try {
      const productData = buildProductFormData(data);
      const response: any = await productService.update(id, productData);

      runInAction(() => {
        this.products = this.products.map((p) =>
          p.id === id ? response.product : p
        );
      });
      if (response.message) {
        toast.success(response.message);
      }
      await this.fetchStats();
      return response?.product;
    } catch (error) {
      // throw error;
    } finally {
      runInAction(() => {
        this.loadingForm = false;
      });
    }
  }

  // async saveProduct(data: Partial<Product> | FormData) {
  //   const isFormData = data instanceof FormData;
  //   const id = isFormData ? (data as FormData).get("id") : data.id;
  //   // console.log("id =>", id);

  //   this.loadingForm = true;
  //   try {
  //     let payload: Partial<Product> | FormData = data;

  //     if (id && !isFormData) {
  //       const original = this.products.find((p) => p.id === id);
  //       if (original) {
  //         payload = Object.fromEntries(
  //           Object.entries(data).filter(([key, value]) => {
  //             return value !== (original as any)[key];
  //           })
  //         );
  //       }
  //     }
  //     const savedProduct = id
  //       ? await productService.update(Number(id), payload)
  //       : await productService.create(payload);

  //     runInAction(() => {
  //       if (id) {
  //         this.products = this.products.map((p) =>
  //           p.id === id ? savedProduct : p
  //         );
  //       } else {
  //         this.products = [...this.products, savedProduct];
  //       }
  //     });

  //     toast.success(id ? "Produit mis à jour !" : "Produit créé !");

  //     return savedProduct;
  //   } catch (error) {
  //     // throw error;
  //   } finally {
  //     runInAction(() => {
  //       this.loadingForm = false;
  //     });
  //   }
  // }

  async fetchProductById(id: number) {
    try {
      const res = await productService.getById(Number(id));
      runInAction(() => {
        this.products.push(res);
      });
      return res;
    } catch (err) {
      // console.error("failed =>", err);
      return null;
    }
  }

  async deleteProduct(id: number) {
    this.loadingDeletesItem.set(id, true);
    try {
      // console.log("id =>", id);
      await productService.delete(id);
      // console.log("result =>", result);
      await this.fetchStats();
      runInAction(() => {
        this.products = this.products.filter((p) => p.id !== id);
      });
      toast.success("Produit supprimé !");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      runInAction(() => {
        this.loadingDeletesItem.delete(id);
      });
    }
  }

  async updateProductStatus(productId: number, isActive: boolean) {
    this.loadingUpdatesItem.set(productId, true);

    try {
      await productService.update(productId, { isActive });
      runInAction(() => {
        this.products = this.products.map((p) =>
          p.id === productId ? { ...p, isActive } : p
        );
      });
      // await this.fetchStats();
      toast.success(`Produit ${isActive ? "activé" : "désactivé"} !`);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
      throw error;
    } finally {
      runInAction(() => {
        this.loadingUpdatesItem.delete(productId);
      });
    }
  }

  async fetchProductStats(
    productId: number,
    period: "day" | "week" | "month" = "day"
  ) {
    if (!authStore.isAuthenticated()) return;
    this.loadingProductStats = true;

    try {
      const res = await productService.getProductStats(productId, period);
      runInAction(() => {
        this.productStats = res;
      });
    } catch (err: any) {
      runInAction(() => {
        this.productStats = null;
      });
      throw err;
      // toast.error(
      //   err.response?.data?.message ||
      //     "Erreur lors du chargement des statistiques du produit"
      // );
    } finally {
      runInAction(() => {
        this.loadingProductStats = false;
      });
    }
  }

  setPage(page: number) {
    this.currentPage = page;
    this.fetchProducts({
      page: this.currentPage,
      limit: this.rowsPerPage,
      ...this.filters,
    });
  }

  setRowsPerPage(limit: number) {
    this.rowsPerPage = limit;
    this.currentPage = 1;
    this.fetchProducts({
      page: this.currentPage,
      limit: this.rowsPerPage,
      ...this.filters,
    });
  }

  setFilters(newFilters: Partial<typeof this.filters>) {
    this.filters = { ...this.filters, ...newFilters };
    this.currentPage = 1;
    this.fetchProducts({
      page: this.currentPage,
      limit: this.rowsPerPage,
      ...this.filters,
    });
  }

  // Helper methods
  isProductUpdating = (id: number) => this.loadingUpdatesItem.get(id) || false;
  isProductDeleting = (id: number) => this.loadingDeletesItem.get(id) || false;
  clearProductStats() {
    this.productStats = null;
    this.loadingProductStats = false;
  }
}

const productStore = new ProductStore();
export default productStore;
